/* eslint-disable react/no-array-index-key */
import { CorrMatrixData } from '@/api/workOrders/types';
import {
  Box,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from '@mui/material';
import { memo, useMemo } from 'react';

import { GroupStatusIcon } from '../../components/StatusIndicators';
import {
  CellCaption,
  CellContent,
  CellHeader,
  headerBorder,
  RowCaption,
  StyledTable,
} from '../../styles';
import { isGroupComplete } from '../../utils/completionUtils';
import { VariableDescriptionSection } from '../types';

interface CorrelationMatrixProps {
  variableDescriptions: VariableDescriptionSection;
  correlation: CorrMatrixData[];
}

export const CorrelationMatrix = memo(function CorrelationMatrix(
  props: CorrelationMatrixProps
) {
  const { variableDescriptions, correlation } = props;

  const allVars = useMemo(
    () => [
      variableDescriptions.dependent_variable,
      ...variableDescriptions.independent_variables,
    ],
    [
      variableDescriptions.dependent_variable,
      variableDescriptions.independent_variables,
    ]
  );

  const getCorrelationValue = (rowIndex: number, colIndex: number): number => {
    if (!Array.isArray(correlation)) return 0;
    return correlation[rowIndex]?.data?.[colIndex]?.isolated ?? 0;
  };

  const isolatedValues = correlation.flatMap((row) =>
    row.data.map((d) => d.isolated)
  );
  const isCorrelationComplete =
    isolatedValues.length > 0 && isGroupComplete(...isolatedValues);

  return (
    <Box id="inf-correlation">
      <Box my={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <GroupStatusIcon isComplete={isCorrelationComplete} />
          <Typography my={3} fontWeight={600}>
            Correlações
          </Typography>
        </Box>
      </Box>
      {allVars.length < 2 ? (
        <Typography mb={1} color="#707070">
          Adicione ao menos uma variável independente para visualizar as
          correlações parciais.
        </Typography>
      ) : (
        allVars.slice(0, -1).map((currentVar, rowIndex) => {
          const remainingVars = allVars.slice(rowIndex + 1);
          return (
            <TableContainer key={`table-${rowIndex}`} sx={{ mb: 2 }}>
              <StyledTable aria-label={`tabela-correlações-${rowIndex}`}>
                <TableHead>
                  <RowCaption>
                    <CellCaption sx={headerBorder} width="50%">
                      {`Correlações Parciais para ${
                        currentVar.name || `Variável ${rowIndex + 1}`
                      }`}
                    </CellCaption>
                    <CellCaption sx={headerBorder} width="50%">
                      (isoladas¹)
                    </CellCaption>
                  </RowCaption>
                </TableHead>
                <TableBody>
                  {remainingVars.map((targetVar, colIndex) => {
                    const targetVarIndex = rowIndex + 1 + colIndex;
                    return (
                      <TableRow key={`row-${rowIndex}-${colIndex}`}>
                        <CellHeader
                          align="center"
                          sx={{
                            backgroundColor: (theme) =>
                              alpha(theme.palette.primary.light, 0.1),
                          }}
                        >
                          {targetVar.name || `Variável ${targetVarIndex + 1}`}
                        </CellHeader>
                        <CellContent align="center">
                          <Typography fontWeight={600}>
                            {getCorrelationValue(rowIndex, colIndex) || '-'}
                          </Typography>
                        </CellContent>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </StyledTable>
            </TableContainer>
          );
        })
      )}
      {allVars.length > 1 && (
        <Typography mt={2} mb={1} fontSize={14} color="primary.dark">
          ¹ valores entre -1 e 1
        </Typography>
      )}
    </Box>
  );
});
